import { RoleSchema } from '@developerz/domain';
import { createMemo, createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { setAdminMemberRole } from '../lib/admin';
import { createConfirmGate } from '../lib/confirm-gate';
import { createKeyedMutation } from '../lib/keyed-mutation';
import type { OperatorRefusal } from '../lib/operator-credential';
import type { OrgRole } from '../lib/org-members';
import { ConfirmDialog } from './ConfirmDialog';
import { OperatorRefusalAlert } from './OperatorRefusalAlert';
import { RolePill } from './RolePill';

// The staff role control on one membership (#5727) — the org-detail member list
// and the user page both render it. The chip is the tenant roster's own
// `<RolePill/>`; staff may set any of the four roles, so every one is offered.
//
// A pick ASKS first (`createConfirmGate`, the console's pattern for a consequential
// write) and the confirm sends `PATCH /v1/admin/accounts/:id/members/:userId` on an
// `admin:accounts` operator credential (`setAdminMemberRole`). The server keeps the
// org's last owner (409 `last_owner`, said as a sentence here); a door refusal
// renders `<OperatorRefusalAlert/>`. On success `onChanged` re-reads — the chip
// never paints the picked role from the echo.

const ROLES: readonly OrgRole[] = RoleSchema.options;

export function AdminMemberRole(props: {
  readonly accountId: string;
  readonly userId: string;
  readonly login: string;
  /** The org's name, for the confirm. */
  readonly org: string;
  /** The server's role string; one outside `RoleSchema` renders read-only. */
  readonly memberRole: string;
  readonly token: string | null;
  readonly onChanged: () => void;
}): JSX.Element {
  const [refusal, setRefusal] = createSignal<OperatorRefusal | null>(null);
  const act = createKeyedMutation({
    fallback: t('admin.member_role_failed'),
    onStart: () => setRefusal(null),
  });
  const change = (role: OrgRole): Promise<void> =>
    act.run('role', async () => {
      const out = await setAdminMemberRole(props.accountId, props.userId, role, props.token);
      if (out.kind !== 'ok') return void setRefusal(out.kind);
      props.onChanged();
    });
  const asking = createConfirmGate<OrgRole>({ act: change });
  // The LAST asked role: `pending()` empties as the write settles, and the dialog
  // keeps rendering through its close animation.
  const asked = createMemo<OrgRole | null>((last) => asking.pending() ?? last, null);
  const current = (): OrgRole | null => {
    const parsed = RoleSchema.safeParse(props.memberRole);
    return parsed.success ? parsed.data : null;
  };

  return (
    <div class="admin-row__stack">
      <Show when={current()} fallback={<span>{props.memberRole}</span>}>
        {(role) => (
          <RolePill
            role={role()}
            editable
            options={ROLES}
            busy={act.isBusy('role')}
            login={props.login}
            onChange={asking.ask}
          />
        )}
      </Show>
      <OperatorRefusalAlert refusal={refusal()} />
      <Show when={act.error()}>
        {(msg) => (
          <p class="field__error" role="alert">
            {msg()}
          </p>
        )}
      </Show>
      <ConfirmDialog
        open={asking.pending() !== null}
        title={t('admin.member_role_title', {
          login: props.login,
          org: props.org,
          role: t(`org_members.role_${asked() ?? 'member'}`),
        })}
        desc={t('admin.member_role_desc')}
        cancelLabel={t('admin.confirm_cancel')}
        actionLabel={t('admin.member_role_confirm')}
        onCancel={asking.cancel}
        onConfirm={() => void asking.confirm()}
      />
    </div>
  );
}
